import React from 'react'
import styled from 'styled-components'
import { Typography, Tabs } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Input, Tab } from 'sputnik-ui'
import trackGoal from 'utils/metrika'
import {
  setFilter,
  selectFilters,
  selectOnlineCount,
  selectOfflineCount,
  selectTotalInstalledCount,
} from 'features/CamerasView/camerasViewSlice'

const HeaderWrapper = styled.div`
  margin-bottom: 32px;
`

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
`

const TitleSection = styled.div`
  display: flex;
  align-items: baseline;
  gap: 16px;
`

const Title = styled(Typography)`
  && {
    font-family: 'Inter', 'Open Sans', sans-serif;
    font-size: 24px;
    font-weight: 700;
    color: #051125;
  }
`

const Subtitle = styled(Typography)`
  && {
    font-family: 'Open Sans', sans-serif;
    font-size: 13px;
    color: #8f9bb3;
    margin-bottom: 16px;
  }
`

const Stats = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const StatItem = styled.span`
  font-family: 'Open Sans', sans-serif;
  font-size: 13px;
  color: #8f9bb3;
  display: flex;
  align-items: center;
  gap: 6px;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(p) => p.color || '#8f9bb3'};
  }
`

const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

const TabsWrapper = styled.div`
  display: flex;
  align-items: center;
  min-height: 44px;
`

const SearchWrapper = styled.div`
  margin-left: auto;
  width: 256px;
`

function CamerasHeader() {
  const dispatch = useDispatch()
  const { t } = useTranslation(['cameras_view'])
  const filters = useSelector(selectFilters)
  const onlineCount = useSelector(selectOnlineCount)
  const offlineCount = useSelector(selectOfflineCount)
  const total = useSelector(selectTotalInstalledCount)

  const handleStatusFilter = (status) => {
    trackGoal('control_cameras_change_filter', {
      filter: status,
      has_search: filters.search.trim().length >= 3,
    })
    dispatch(setFilter({ status }))
  }

  const statusTabs = ['all', 'online', 'offline']
  const activeTab = Math.max(0, statusTabs.indexOf(filters.status))

  const handleTabChange = (event, index) => {
    handleStatusFilter(statusTabs[index] || 'all')
  }

  const handleSearch = (e) => {
    dispatch(setFilter({ search: e.target.value }))
  }

  const tabs = [
    { key: 'all', label: t('cameras_view:all'), count: total },
    { key: 'online', label: t('cameras_view:online'), count: onlineCount },
    { key: 'offline', label: t('cameras_view:offline'), count: offlineCount },
  ]

  return (
    <HeaderWrapper>
      <TopRow>
        <TitleSection>
          <Title>{t('cameras_view:title')}</Title>
          <Stats>
            <StatItem color="#051125">{total}</StatItem>
            <StatItem color="#0FBA81">{onlineCount}</StatItem>
            <StatItem color="#FF3D71">{offlineCount}</StatItem>
          </Stats>
        </TitleSection>
      </TopRow>
      <Subtitle>{t('cameras_view:installed_only')}</Subtitle>
      <FiltersRow>
        <TabsWrapper>
          <Tabs
            onChange={handleTabChange}
            value={activeTab}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map(({ key, label, count }, index) => (
              <Tab
                key={key}
                value={index}
                label={`${label} (${count})`}
                style={{ marginRight: '0.5rem' }}
              />
            ))}
          </Tabs>
        </TabsWrapper>
        <SearchWrapper>
          <Input
            placeholder={t('cameras_view:search_placeholder')}
            value={filters.search}
            onChange={handleSearch}
            style={{ width: '100%' }}
          />
        </SearchWrapper>
      </FiltersRow>
    </HeaderWrapper>
  )
}

export default CamerasHeader
